import { useState } from "react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { fmt } from "@/lib/format";
import { X, Upload, Wallet, CheckCircle2, AlertCircle, ArrowLeft } from "lucide-react";

export default function KasaImportDialog({ onClose, onDone }) {
  const [file, setFile] = useState(null);
  const [loading, setLoading] = useState(false);
  const [preview, setPreview] = useState(null);

  const send = async (apply) => {
    if (!file) { toast.error("Lütfen bir Kasa Excel dosyası seçin"); return; }
    setLoading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post(`/cari/kasa-import?apply=${apply}`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      if (apply) {
        const g = data.gencer;
        const gmsg = g && !g.hata && g.gun > 0 ? ` · Gençer kasaya ${g.gun} gün eklendi` : (g && g.atlanan_tarihler?.length ? ` · Gençer: ${g.atlanan_tarihler.length} gün zaten vardı` : "");
        toast.success(`${data.yazilan_odeme} ödeme işlendi (${data.ozet.planlanan} plan, ${data.ozet.bitti} bitti)${gmsg}`);
        onDone();
      } else {
        if (data.toplam_odeme === 0) { toast.error("Dosyada S.H.K.K taksit ödemesi bulunamadı"); setFile(null); }
        else setPreview(data);
      }
    } catch (e) {
      toast.error(e.response?.data?.detail || "İşlem başarısız");
    } finally { setLoading(false); }
  };

  return (
    <div className="fixed inset-0 z-[100] flex items-start justify-center bg-black/40 p-4 overflow-y-auto">
      <div className={`bg-white border border-[#E7DECC] rounded-sm w-full my-10 ${preview ? "max-w-4xl" : "max-w-lg"}`} data-testid="kasa-import-dialog">
        <div className="flex items-center justify-between px-6 py-4 border-b border-[#E7DECC]">
          <h3 className="font-heading text-xl font-bold tracking-tight flex items-center gap-2">
            <Wallet className="w-5 h-5 text-[#B8933F]" /> {preview ? "Kasa Eşleştirme Önizlemesi" : "Kasa Excel'i ile Taksit Ödemesi İşle"}
          </h3>
          <button onClick={onClose} aria-label="Kapat" data-testid="kasa-import-close" className="text-[#8C8377] hover:text-[#26231E]"><X className="w-5 h-5" /></button>
        </div>

        {!preview ? (
          <div className="p-6">
            <div className="border border-[#E7DECC] bg-[#F3ECDD] rounded-sm p-4 mb-5 text-sm text-[#8C8377] space-y-1">
              <p>Aylık Kasa Excel'ini yükleyin. Günlük sayfalardan <b>S.H.K.K</b> taksit ödemeleri okunur.</p>
              <p>İsimle cari müşteri bulunur (İcra/dışı isimler atlanır). Vade etiketi uygulanır: <b>"12 AY"</b>→Aralık vadesi, <b>"3 AY"</b>→Mart, <b>"4-5 AY KISMİ"</b>→4. ay tam + kalan 5. aya, <b>"BİTTİ"</b>→kalan taksitleri kapatır.</p>
              <p>Önce <b>önizleyin</b>, sonra onaylayın. Aynı ödeme tekrar yüklenirse atlanır.</p>
              <p className="text-[#B8933F]">Aynı Excel otomatik olarak <b>Gençer Ana Kasa</b>'ya da işlenir (01.10.2026 ve sonrası; zaten yüklü günler atlanır) — ayrıca Gençer kasaya yüklemenize gerek yok.</p>
            </div>
            <label htmlFor="kasa-import-file" className="flex flex-col items-center justify-center gap-2 border-2 border-dashed border-[#E7DECC] rounded-sm py-8 cursor-pointer hover:border-[#B8933F] transition-colors duration-150">
              <Wallet className="w-8 h-8 text-[#B8933F]" />
              <span className="text-sm text-[#8C8377]">{file ? file.name : "Kasa Excel seçmek için tıklayın (.xlsx)"}</span>
              <input id="kasa-import-file" type="file" accept=".xlsx" className="hidden" data-testid="kasa-import-file-input" onChange={(e) => setFile(e.target.files?.[0] || null)} />
            </label>
            <div className="flex justify-end gap-3 mt-6">
              <button onClick={onClose} className="px-4 py-2 border border-[#E7DECC] rounded-sm text-sm font-medium hover:bg-[#EFE7D6]">İptal</button>
              <button onClick={() => send(false)} disabled={loading} data-testid="kasa-preview-button" className="flex items-center gap-1.5 px-6 py-2 bg-[#B8933F] hover:bg-[#9C7A33] text-white rounded-sm text-sm font-medium disabled:opacity-60">
                <Upload className="w-4 h-4" /> {loading ? "İşleniyor..." : "Önizle"}
              </button>
            </div>
          </div>
        ) : (
          <div className="p-6">
            <div className="flex flex-wrap items-center gap-4 mb-4 text-sm">
              <div className="flex items-center gap-1.5"><CheckCircle2 className="w-4 h-4 text-[#2F6E4E]" /><b>{preview.ozet.planlanan}</b> planlanan</div>
              <div className="flex items-center gap-1.5"><CheckCircle2 className="w-4 h-4 text-[#B8933F]" /><b>{preview.ozet.bitti}</b> bitti</div>
              <div className="flex items-center gap-1.5"><AlertCircle className="w-4 h-4 text-[#B8933F]" /><b>{preview.ozet.cozulemeyen}</b> çözülemeyen</div>
              <div className="flex items-center gap-1.5 text-[#8C8377]"><b>{preview.ozet.atlanan}</b> atlanan (cari değil/işlenmiş)</div>
              <span className="text-[#8C8377]">Toplam {preview.toplam_odeme} ödeme satırı</span>
            </div>

            <div className="border border-[#E7DECC] rounded-sm overflow-auto max-h-[45vh]" data-testid="kasa-preview-table">
              <table className="w-full text-sm">
                <thead className="sticky top-0 bg-[#F3ECDD]">
                  <tr className="border-b border-[#E7DECC]">
                    <th className="px-2 py-2 kicker text-left">Gün</th>
                    <th className="px-2 py-2 kicker text-left">Müşteri</th>
                    <th className="px-2 py-2 kicker text-left">Etiket</th>
                    <th className="px-2 py-2 kicker text-right">Tutar</th>
                    <th className="px-2 py-2 kicker text-left">İşlenecek Vade(ler)</th>
                  </tr>
                </thead>
                <tbody>
                  {preview.planlanan.map((r, i) => (
                    <tr key={`p${i}`} className="border-b border-[#E7DECC]" data-testid={`kasa-plan-${i}`}>
                      <td className="px-2 py-1.5 tabular-nums">{r.gun}</td>
                      <td className="px-2 py-1.5 font-medium">{r.isim}</td>
                      <td className="px-2 py-1.5 text-[#8C8377]">{r.label}</td>
                      <td className="px-2 py-1.5 text-right tabular-nums">{fmt(r.tutar)}</td>
                      <td className="px-2 py-1.5 text-[#248A3D]">{r.dagilim.map((d) => `${d.vade} ← ${fmt(d.tutar)}`).join("  |  ")}{r.artan > 0 ? `  (artan ${fmt(r.artan)})` : ""}</td>
                    </tr>
                  ))}
                  {preview.bitti.map((r, i) => (
                    <tr key={`b${i}`} className="border-b border-[#E7DECC] bg-[#B8933F]/5" data-testid={`kasa-bitti-${i}`}>
                      <td className="px-2 py-1.5 tabular-nums">{r.gun}</td>
                      <td className="px-2 py-1.5 font-medium">{r.isim}</td>
                      <td className="px-2 py-1.5 text-[#B8933F] font-semibold">BİTTİ</td>
                      <td className="px-2 py-1.5 text-right tabular-nums">{fmt(r.tutar)}</td>
                      <td className="px-2 py-1.5 text-[#B8933F]">Kalan {r.kapanacak} taksit kapatılacak</td>
                    </tr>
                  ))}
                  {preview.cozulemeyen.map((r, i) => (
                    <tr key={`u${i}`} className="border-b border-[#E7DECC] bg-[#B8933F]/5" data-testid={`kasa-unresolved-${i}`}>
                      <td className="px-2 py-1.5 tabular-nums">{r.gun}</td>
                      <td className="px-2 py-1.5 font-medium">{r.isim}</td>
                      <td className="px-2 py-1.5 text-[#8C8377]">{r.label}</td>
                      <td className="px-2 py-1.5 text-right tabular-nums">{fmt(r.tutar)}</td>
                      <td className="px-2 py-1.5 text-[#B25000]">{r.sebep} — elle bakın</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {preview.atlanan.length > 0 && (
              <details className="mt-3">
                <summary className="cursor-pointer text-sm text-[#8C8377]">Atlananları göster ({preview.atlanan.length})</summary>
                <div className="mt-2 max-h-40 overflow-y-auto text-xs text-[#8C8377] space-y-0.5 border border-[#E7DECC] rounded-sm p-2">
                  {preview.atlanan.map((r, i) => (<div key={i}>{r.gun} · {r.isim} · {fmt(r.tutar)} — {r.sebep}</div>))}
                </div>
              </details>
            )}

            <div className="flex justify-between gap-3 mt-6">
              <button onClick={() => setPreview(null)} className="flex items-center gap-1.5 px-4 py-2 border border-[#E7DECC] rounded-sm text-sm font-medium hover:bg-[#EFE7D6]"><ArrowLeft className="w-4 h-4" /> Geri</button>
              <button onClick={() => send(true)} disabled={loading || (preview.ozet.planlanan + preview.ozet.bitti) === 0} data-testid="kasa-apply-button" className="flex items-center gap-1.5 px-6 py-2 bg-[#B8933F] hover:bg-[#9C7A33] text-white rounded-sm text-sm font-medium disabled:opacity-50">
                <CheckCircle2 className="w-4 h-4" /> {loading ? "İşleniyor..." : `Onayla ve İşle (${preview.ozet.planlanan + preview.ozet.bitti})`}
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
